<template>
  <div class="users-container">
    <Header :title="headerTitle" :user="currentUser" />

    <main v-if="currentUser?.isAdmin" class="page-main">
      <div class="action-btn-bar">
        <button v-if="currentUser?.isAdmin || currentUser?.type==='parent'" @click="createNewUser" class="action-btn construct-btn">New User</button>
      </div>
      <div v-if="loading" class="loading">Loading users...</div>
      <div v-else-if="users.length === 0" class="empty-state">
        <p>No users found</p>
      </div>
      <div v-else class="tiles-container">
        <div
          v-for="user in users"
          :key="user.id"
          class="tile"
          @click="editUser(user.id)"
          style="cursor:pointer;display:flex;align-items:center;gap:1rem;"
        >
          <div style="width:56px;height:56px;flex-shrink:0;display:flex;align-items:center;justify-content:center;">
            <img v-if="user.avatar_url" :src="user.avatar_url" alt="Profile" style="width:56px;height:56px;object-fit:cover;border-radius:50%;border:2px solid #eee;" />
            <img v-else-if="user.profileImageDataUrl" :src="user.profileImageDataUrl" alt="Profile" style="width:56px;height:56px;object-fit:cover;border-radius:50%;border:2px solid #eee;" />
            <span v-else style="width:56px;height:56px;display:flex;align-items:center;justify-content:center;font-size:2rem;border-radius:50%;background:#eee;color:#666;font-weight:600;">{{ (user.name || 'U').charAt(0).toUpperCase() }}</span>
          </div>
          <div class="user-main-info">
            <div style="font-size:1.1rem;font-weight:500;color:#333;">{{ user.name }}</div>
            <div style="font-size:0.95rem;color:#666;">
              <span>{{ user.type ? user.type.charAt(0).toUpperCase() + user.type.slice(1) : 'Unknown' }}</span>
              <span v-if="user.isAdmin" style="margin-left:0.5rem;color:#43a047;font-weight:600;">Admin</span>
            </div>
          </div>
        </div>
      </div>
    </main>
    
    <main v-else-if="currentUser && !currentUser.isAdmin" class="page-main">
      <div class="unauthorized">
        <h2>Access Denied</h2>
        <p>You need admin privileges to view this page.</p>
        <button @click="goDashboard" class="action-btn">Go to Dashboard</button>
      </div>
    </main>
    
    <main v-else class="page-main">
      <div class="loading">Loading...</div>
    </main>
  </div>
</template>

<script setup lang="ts">

import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import Header from '../components/Header.vue'
import { type User } from '../services/database'


const router = useRouter()
const users = ref<User[]>([])
const loading = ref(false)
const currentUser = ref<User | null>(null)
const headerTitle = ref('Users')

const goProfile = () => {
  router.push({ name: 'edit-user', params: { id: currentUser.value?.id } })
}

// Admin filters removed per new requirements

const formatDate = (date: Date): string => {
  return new Date(date).toLocaleDateString()
}

const displayType = (u: any) => {
  return u.type || (u.role || 'unknown')
}

const goBack = () => {
  router.push('/dashboard')
}

const goDashboard = () => {
  router.push('/dashboard')
}

const createNewUser = () => {
  router.push({ name: 'edit-user' })
}


const createNewFamily = () => {
  // open the edit-user route with a query indicating we're creating a family
  router.push({ name: 'edit-user', query: { type: 'family' } })
}

const editUser = (id: string) => {
  router.push({ name: 'edit-user', params: { id } })
}

const deleteUser = async (id: string) => {
  // only admins can delete
  if (!currentUser.value?.isAdmin) {
    alert('Only admins can delete users')
    return
  }

  if (id === currentUser.value?.id) {
    alert('Cannot delete your own account')
    return
  }

  if (confirm('Are you sure you want to delete this user?')) {
    try {
      // Get session token from sessionStorage for authorization
      const userStr = sessionStorage.getItem('currentUser');
      const userData = userStr ? JSON.parse(userStr) : null;
      const sessionToken = userData?.sessionToken;
      
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      
      if (sessionToken) {
        headers['Authorization'] = `Bearer ${sessionToken}`;
      }
      
      await fetch(`${API_BASE_URL}/api/users/${id}`, { 
        method: 'DELETE',
        headers
      });
      await loadUsers()
    } catch (error) {
      console.error('Failed to delete user:', error)
    }
  }
}

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000';

const loadUsers = async () => {
  loading.value = true;
  try {
    // Get session token from sessionStorage for authorization
    const userStr = sessionStorage.getItem('currentUser');
    const userData = userStr ? JSON.parse(userStr) : null;
    const sessionToken = userData?.sessionToken;
    
    console.log('Users: Loading users with session token:', sessionToken ? `${sessionToken.substring(0, 8)}...` : 'NONE');
    console.log('Users: API URL:', `${API_BASE_URL}/api/users`);
    
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    };
    
    if (sessionToken) {
      headers['Authorization'] = `Bearer ${sessionToken}`;
    }
    
    console.log('Users: Request headers:', headers);
    
    const response = await fetch(`${API_BASE_URL}/api/users`, {
      headers
    });
    
    console.log('Users: Response status:', response.status, response.statusText);
    console.log('Users: Response ok:', response.ok);
    
    if (!response.ok) {
      throw new Error(`Failed to load users: ${response.status} ${response.statusText}`);
    }
    
    const all = await response.json();
    console.log('Users: Loaded users from API:', all);
    
    if (currentUser.value && currentUser.value.type === 'parent') {
      headerTitle.value = 'Family';
      const accountId = currentUser.value.account_id || currentUser.value.id;
      users.value = all.filter((u: any) => (u.account_id || null) === accountId);
    } else {
      headerTitle.value = 'Users';
      users.value = all;
    }
  } catch (error) {
    console.error('Users: Failed to load users - Full error:', error);
    console.error('Users: Error type:', error instanceof Error ? error.constructor.name : typeof error);
    console.error('Users: Error message:', error instanceof Error ? error.message : String(error));
    
    if (error instanceof TypeError && error.message.includes('fetch')) {
      console.error('Users: Network error - Is the backend server running on', API_BASE_URL + '?');
    }
  } finally {
    loading.value = false;
  }
}

onMounted(async () => {
  // Load user data from sessionStorage (no session validation needed)
  try {
    const userStr = sessionStorage.getItem('currentUser');
    if (userStr) {
      currentUser.value = JSON.parse(userStr);
      console.log('Users: User loaded from sessionStorage:', currentUser.value);
    } else {
      console.log('Users: No user data in sessionStorage');
    }
  } catch (error) {
    console.error('Users: Error parsing user data from sessionStorage:', error);
  }
  
  console.log('Users: Loading without session validation');
  await loadUsers();
})

// filters removed: no watchers
</script>

<style scoped>
/* ...existing code... */
</style>
